import React, { memo } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
  botInfoStore,
  setOpenStatus,
  setPipPendingConfirm,
} from "../../Store/Slices/socket/BotInfoSlice";
import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../Services/IframeHostService/models";

// Shown at the small size the loader boots the PiP window to (320x220 —
// see openPipWindow()), matching Chrome's own "Keep chatting in this
// window?" dialog — filling that window edge to edge, the same way
// Chrome's own dialog fills its window with no border/margin around it.
// "Keep chatting here" always runs (no skip path), so the fresh user
// activation that click supplies is always available for the loader's
// resizeTo() call that grows the window to the full chat panel size
// afterward.
const StyledPipConfirmCard = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;

  h2 {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
    padding: 16px 16px 6px;
    color: #1a1a1a;
    text-align: center;
  }
  p {
    font-size: 13px;
    line-height: 1.4;
    color: #444;
    margin: 0;
    padding: 0 16px 16px;
    text-align: center;
  }

  .pipConfirmActions {
    margin-top: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 20px;
  }
  button {
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 12px 0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .primary {
    background: #1a1a1a;
    color: #fff;
  }
  .secondary {
    background: #e8e8e8;
    color: #1a1a1a;
  }
`;

const PipConfirmScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { location } = useAppSelector(botInfoStore);
  const commService = React.useMemo(
    () => HostWindowCommService.getInstance(),
    []
  );

  // window.top.location is cross-origin here (the widget always runs
  // inside an iframe on the host page) and throws just from being read,
  // which a plain try/catch around the whole chain still catches — but it
  // means that branch could never actually succeed, always falling through
  // to document.referrer or "this site". The loader already sends the
  // host page's own location in CHATBOT_START (see BotInfoSlice), so use
  // that directly instead of guessing at it from inside the iframe.
  const siteLabel = (() => {
    try {
      return (
        (location && new URL(location).hostname) ||
        (document.referrer && new URL(document.referrer).hostname) ||
        "this site"
      );
    } catch (e) {
      return "this site";
    }
  })();

  const handleContinueHere = () => {
    // Grows the PiP window from the small confirm-card size to the full
    // chat panel size — this click supplies the fresh user activation
    // resizeTo() requires, same as requestWindow() itself.
    commService.postMessage({
      event_type: OEvents.CHATBOT_PIP_CONFIRMED,
      data: {},
    });
    dispatch(setPipPendingConfirm(false));
    dispatch(setOpenStatus(true));
  };

  const handleCloseWindow = () => {
    commService.postMessage({
      event_type: OEvents.CHATBOT_CLOSE_PIP,
      data: {},
    });
  };

  return (
    <StyledPipConfirmCard>
      <h2>Keep chatting in this window?</h2>
      <p>
        Your conversation on {siteLabel} stays open while you switch tabs or
        apps.
      </p>
      <div className="pipConfirmActions">
        <button className="primary" onClick={handleContinueHere}>
          Keep chatting here
        </button>
        <button className="secondary" onClick={handleCloseWindow}>
          Close window
        </button>
      </div>
    </StyledPipConfirmCard>
  );
};

export default memo(PipConfirmScreen);
